import { activeChainFor, cheminDe, type NavEntry, type NavFamily } from '../navigation/site-navigation.js'

/**
 * LE MENU FLOTTANT (03/11/2026, validé par Hugo sur maquette animée) — le
 * modèle, sans navigateur.
 *
 *  · le RAIL : Favoris, Accueil et les 7 familles ; il s'élargit au survol
 *    et montre leurs noms ;
 *  · le PANNEAU des modules d'une famille, une fenêtre flottante en face de
 *    la famille survolée ;
 *  · la fenêtre des SOUS-MENUS d'un module, en face du module survolé
 *    (`entreesDuModule`) ; les mêmes entrées sont les ONGLETS en haut de la
 *    page du module (`ongletsDuModule`).
 *
 * Le filtre « ce que le menu montre » est celui de l'ancienne barre :
 * `showInSidebar` différent de false, une route, pas PLANNED, et le droit.
 */

export type Peut = (perm?: string) => boolean

const montree = (e: NavEntry, peut: Peut): boolean =>
  e.showInSidebar !== false && !!e.route && e.state !== 'PLANNED' && peut(e.permission)

/** Les modules qu'une famille montre dans le panneau. */
export function modulesVisibles(f: NavFamily, peut: Peut): NavEntry[] {
  return f.children.filter((c) => montree(c, peut))
}

/** Les fonctions d'un module qui deviennent des onglets. */
export function fonctionsVisibles(m: NavEntry, peut: Peut): NavEntry[] {
  return (m.children ?? []).filter((x) => montree(x, peut))
}

/** Une famille vide pour ce compte n'apparaît pas dans le rail (Accueil, toujours). */
export function familleMontree(f: NavFamily, peut: Peut): boolean {
  return f.id === 'accueil' || modulesVisibles(f, peut).length > 0
}

/**
 * Préférence `sidebar` (valeurs inchangées côté serveur) : « pinned » =
 * menu ÉPINGLÉ, toujours ouvert, dans le flux ; « auto » et « compact » =
 * menu flottant, qui s'ouvre au survol et se referme tout seul.
 */
export function menuEpingle(pref: string | undefined): boolean {
  return pref === 'pinned'
}

export interface Onglet { id: string; label: string; route: string; actif: boolean }

/**
 * Les entrées d'un module — onglets de sa page et fenêtre des sous-menus :
 * « Vue d'ensemble » (sa page, sauf si une fonction visible l'est déjà), puis
 * ses fonctions visibles. Vide quand il n'a aucune fonction visible.
 * L'entrée active se lit sur l'adresse ; sur une fiche rangée sous le module
 * sans être une de ses fonctions, aucune ne l'est.
 */
export function entreesDuModule(mod: NavEntry, adresse: string, peut: Peut): Onglet[] {
  const fns = fonctionsVisibles(mod, peut)
  if (fns.length === 0 || !mod.route) return []
  const chaine = activeChainFor(adresse)
  const ici = chaine[0]?.id === mod.id
  const fnActive = ici ? fns.find((x) => x.id === chaine[1]?.id) : undefined
  const avecEnsemble = !fns.some((x) => x.route === mod.route)
  return [
    ...(avecEnsemble ? [{ id: `${mod.id}--ensemble`, label: 'Vue d’ensemble', route: mod.route, actif: ici && !fnActive && cheminDe(adresse) === cheminDe(mod.route) }] : []),
    ...fns.map((x) => ({ id: x.id, label: x.label, route: x.route!, actif: x === fnActive })),
  ]
}

/** Les onglets du module affiché, ou null quand il n'a aucune fonction visible. */
export function ongletsDuModule(adresse: string, peut: Peut): { module: NavEntry; onglets: Onglet[] } | null {
  const mod = activeChainFor(adresse)[0]
  if (!mod?.route || !peut(mod.permission)) return null
  const onglets = entreesDuModule(mod, adresse, peut)
  return onglets.length ? { module: mod, onglets } : null
}

/**
 * Hauteur d'une fenêtre flottante en face de l'élément survolé : `haut` est
 * la position voulue dans la zone du menu, `hauteur` celle de la fenêtre,
 * `max` celle de la zone. La fenêtre remonte juste assez pour tenir.
 */
export function borner(haut: number, hauteur: number, max: number): number {
  return Math.max(0, Math.min(haut, max - hauteur))
}
